import { AwsRum } from 'aws-rum-web';

// Call this "hook" to access AwsRum in a component. It'll return the rum client if available. Otherwise, null.
export function useAwsRum(): AwsRum | null {
  return loadAwsRum();
}

/*
 * Implementation details below here.
 */

export type AwsRumConfg =
  | {
      applicationId: string;
      applicationVersion: string;
      region: string;
    }
  | {
      applicationId: undefined; // undefined when rum is disabled
    };

// We cache rum in memory once loaded.
let rum: AwsRum | null | undefined;
function loadAwsRum(): AwsRum | null {
  if (rum === undefined) {
    rum = _loadAwsRum();
  }
  return rum;
}

function _loadAwsRum(): AwsRum | null {
  if (typeof window === 'undefined') {
    // We're on the server. We don't use rum on the server currently.
    return null;
  } else {
    // We're on the client, so we'll read configuration set up by BootstrapClientSideMonitoring
    const awsRumConfigElement = document.getElementById('aws-rum-config');

    // For some pages (i.e. error pages), the script tag will not be present. We do our best to soldier on.
    const awsRumConfg = JSON.parse(awsRumConfigElement?.textContent ?? '{}') as AwsRumConfg;

    if (typeof awsRumConfg.applicationId === 'string') {
      // If applicationId is set, create a rum client
      const { applicationId, applicationVersion, region } = awsRumConfg;

      const isLocal = window.location.hostname === 'localhost';
      const endpoint = isLocal
        ? `https://dataplane.rum.${region}.amazonaws.com`
        : `https://${window.location.host}/families/rum`;

      return new AwsRum(applicationId, applicationVersion, region, {
        sessionSampleRate: 1,
        endpoint,
        telemetries: ['errors', 'performance', 'http'],
        allowCookies: true,
        enableXRay: false,
        signing: false,
        releaseId: applicationVersion, // must equal the S3 releaseId folder (image_tag)
      });
    } else {
      // If applicationId is not set, we're not using rum for this environment.
      return null;
    }
  }
}
